import { getFirestore, doc, getDoc } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { parseMarkdown } from "../../utils/markdown.js";
import { AdminPopupService } from "../../services/admin-popup.service.js"; // [FIX] Import Service

const db = getFirestore(app);

// --- Cosmic Styles Injection ---
const styleId = 'info-modal-styles';
if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
        @keyframes border-beam {
            100% { offset-distance: 100%; }
        }
        @keyframes cosmic-pulse {
            0%, 100% { opacity: 0.8; transform: scale(1); filter: drop-shadow(0 0 5px rgba(139, 92, 246, 0.5)); }
            50% { opacity: 1; transform: scale(1.05); filter: drop-shadow(0 0 15px rgba(217, 70, 239, 0.6)); }
        }
        @keyframes star-twinkle {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
        }
        .cosmic-scrollbar::-webkit-scrollbar { width: 6px; }
        .cosmic-scrollbar::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.05); }
        .cosmic-scrollbar::-webkit-scrollbar-thumb { background: linear-gradient(to bottom, #8b5cf6, #d946ef); border-radius: 10px; }
        
        .aurora-text {
            background: linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            text-shadow: 0 0 20px rgba(142, 197, 252, 0.3);
        }
        
        .glass-panel {
            background: rgba(15, 23, 42, 0.6);
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }

        .markdown-cosmic h1 { background: linear-gradient(to right, #ff7e5f, #feb47b); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 800; border-bottom: 2px solid rgba(255,255,255,0.1); padding-bottom: 10px; margin-bottom: 20px; font-size: 2rem; }
        .markdown-cosmic h2 { color: #fff; font-size: 1.5rem; margin-top: 30px; margin-bottom: 15px; display: flex; align-items: center; gap: 10px; }
        .markdown-cosmic h2::before { content: '✨'; font-size: 1.2rem; filter: drop-shadow(0 0 5px #ffd700); }
        /* FAIL-SAFE: Target container and divs directly */
        .markdown-cosmic, .markdown-cosmic p, .markdown-cosmic li, .markdown-cosmic div { color: #e2e8f0; line-height: 1.7; font-size: 1.05rem; }
        .markdown-cosmic strong { color: #f0abfc; text-shadow: 0 0 10px rgba(240, 171, 252, 0.5); }
        .markdown-cosmic blockquote { border-left: 4px solid #a855f7; background: rgba(168, 85, 247, 0.1); padding: 15px; border-radius: 0 12px 12px 0; font-style: italic; color: #d8b4fe; margin: 20px 0; }
        .markdown-cosmic code { background: rgba(255, 255, 255, 0.1); color: #67e8f9; padding: 2px 6px; border-radius: 4px; font-family: 'Fira Code', monospace; }
        .markdown-cosmic ul { list-style: none; padding-left: 10px; }
        .markdown-cosmic ul li::before { content: '🌙'; margin-right: 10px; color: #fcd34d; display: inline-block; transform: scale(0.8); }
        .markdown-cosmic hr { border-color: rgba(255,255,255,0.1); margin: 30px 0; }
        .markdown-cosmic a { color: #38bdf8; text-decoration: none; border-bottom: 1px dashed #38bdf8; transition: all 0.3s; }
        .markdown-cosmic a:hover { color: #7dd3fc; text-shadow: 0 0 10px #38bdf8; border-bottom-style: solid; }
    `;
    document.head.appendChild(style);
}

export const InfoModal = {
    /**
     * Show the Info Modal (Premium Cosmic Version)
     * @param {string} docId - content document ID (e.g., 'terms', 'privacy', 'about')
     * @param {string} titleOverride - optional title override
     */
    show: async (docId, titleOverride) => {
        // 1. Overlay with Deep Space Background
        // 1. Overlay (Behind Header)
        // 1. Overlay (Behind Header)
        const overlay = document.createElement('div');
        overlay.id = 'info-modal-overlay'; // [FIX] ID for programmatic closing
        overlay.className = "fixed inset-0 z-[80] fade-in p-4";
        overlay.style.background = "radial-gradient(circle at center, rgba(20, 10, 40, 0.8) 0%, rgba(5, 5, 10, 0.95) 100%)";
        overlay.style.backdropFilter = "blur(8px)";

        // 2. Wrapper (Top Layer)
        const wrapper = document.createElement('div');
        wrapper.id = 'info-modal-wrapper'; // [FIX] ID for programmatic closing
        wrapper.className = "fixed inset-0 z-[90] flex items-center justify-center pointer-events-none p-4";

        // 3. Modal Card (Content)
        const modal = document.createElement('div');
        modal.className = "w-full max-w-2xl h-[85vh] glass-panel rounded-3xl flex flex-col overflow-hidden animate-scale-in relative border-0 pointer-events-auto";
        // Inner gradient glow
        modal.style.boxShadow = "0 0 50px rgba(124, 58, 237, 0.2), inset 0 0 20px rgba(124, 58, 237, 0.05)";

        // ... (Header creation remains same, just append to modal)
        const header = document.createElement('div');
        header.className = "px-8 py-5 flex justify-between items-center border-b border-white/10 relative overflow-hidden";
        header.style.background = "linear-gradient(to right, rgba(139, 92, 246, 0.1), transparent)";
        header.innerHTML = `
            <div class="flex items-center gap-3 relative z-10">
                <div class="w-10 h-10 rounded-full bg-gradient-to-tr from-violet-600 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                    <span class="material-symbols-outlined text-white text-xl">auto_stories</span>
                </div>
                <div>
                    <h3 class="font-bold text-xl aurora-text tracking-wide" id="modal-title">
                        <span class="opacity-70">Loading...</span>
                    </h3>
                    <p class="text-[10px] text-zinc-400 font-mono tracking-widest uppercase opacity-80">SSULWHA UNIVERSE</p>
                </div>
            </div>
            <button id="btn-close-info" class="w-10 h-10 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all duration-300 relative z-10 group">
                <span class="material-symbols-outlined text-2xl group-hover:rotate-90 transition-transform duration-300">close</span>
            </button>
            
            <!-- Animated particles hint -->
            <div class="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-violet-500/10 to-transparent pointer-events-none"></div>
        `;

        // 4. Content Area
        const content = document.createElement('div');
        content.className = "flex-1 overflow-y-auto p-8 lg:p-10 cosmic-scrollbar relative";

        // (Background stars... skipped in diff optimization but effectively same)
        const bgStars = document.createElement('div');
        bgStars.className = "absolute inset-0 pointer-events-none opacity-30";
        bgStars.style.backgroundImage = "radial-gradient(white 1px, transparent 1px)";
        bgStars.style.backgroundSize = "50px 50px";
        content.appendChild(bgStars);

        const contentInner = document.createElement('div');
        contentInner.className = "relative z-10 markdown-cosmic";
        contentInner.innerHTML = `
            <div class="h-full flex flex-col items-center justify-center gap-6 py-20">
                <div class="relative w-20 h-20">
                    <div class="absolute inset-0 rounded-full border-4 border-violet-500/30 border-t-violet-400 animate-spin"></div>
                    <div class="absolute inset-4 rounded-full border-4 border-fuchsia-500/30 border-b-fuchsia-400 animate-spin" style="animation-direction: reverse; animation-duration: 2s;"></div>
                    <div class="absolute inset-[35%] bg-white/20 rounded-full blur-md animate-pulse"></div>
                </div>
                <div class="text-center">
                    <p class="text-lg text-violet-200 font-medium">Downloading Stardust...</p>
                    <p class="text-xs text-violet-400/60 mt-2 font-mono">ESTABLISHING LINK TO ARCHIVE</p>
                </div>
            </div>
        `;
        content.appendChild(contentInner);

        modal.appendChild(header);
        modal.appendChild(content);

        // Append to DOM
        wrapper.appendChild(modal);
        document.body.appendChild(overlay);
        document.body.appendChild(wrapper);

        // --- Actions ---

        const close = () => {
            modal.style.transform = "scale(0.9) translateY(20px)";
            modal.style.opacity = "0";
            overlay.style.opacity = "0";
            setTimeout(() => {
                if (overlay.isConnected) overlay.remove();
                if (wrapper.isConnected) wrapper.remove();
            }, 300);
        };

        // Click outside on overlay
        overlay.onclick = () => close();
        header.querySelector('#btn-close-info').onclick = close;

        // --- Fetch Data ---
        try {
            let mdText = '';
            let displayTitle = titleOverride || docId;

            if (docId === 'whats-new') {
                // Special Handling for What's New (Fetch from 'notices' collection)
                const { collection, getDocs, query, orderBy, limit } = await import("firebase/firestore");
                const q = query(collection(db, 'notices'), orderBy('createdAt', 'desc'), limit(10));
                const snap = await getDocs(q);

                if (!snap.empty) {
                    displayTitle = "What's New";
                    mdText = snap.docs.map(d => {
                        const originalData = d.data();

                        // [FIX] Resolve Translation (v1.36.251)
                        const data = AdminPopupService.resolveTranslation(originalData);

                        const date = data.createdAt ? new Date(data.createdAt.toDate()).toLocaleDateString() : '';

                        // [FIX] Buttonized Link & Auto-Close
                        let linkHtml = '';
                        if (data.linkUrl) {
                            const isExternal = data.linkUrl.startsWith('http');
                            // [FIX] Nuclear close script: Remove both overlay and wrapper by ID
                            const closeScript = "document.getElementById('info-modal-overlay')?.remove(); document.getElementById('info-modal-wrapper')?.remove();";

                            const clickAction = isExternal
                                ? `${closeScript} window.open('${data.linkUrl}', '_blank')`
                                : `${closeScript} (window.router ? window.router.navigate('${data.linkUrl}') : window.location.href='${data.linkUrl}')`;

                            linkHtml = `\n<button onclick="${clickAction}" class="w-full mt-4 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold rounded-xl shadow-lg hover:shadow-violet-500/30 active:scale-95 transition-all flex items-center justify-center gap-2 group"><span class="material-symbols-outlined group-hover:rotate-45 transition-transform">rocket_launch</span> Visit Link</button>`;
                        }

                        // [FIX] Use HTML <img> instead of Markdown to handle filenames with parentheses
                        const imgHtml = data.imageUrl ? `\n<img src="${data.imageUrl}" class="w-full rounded-xl my-4 shadow-lg border border-white/10" alt="Event Image">` : '';

                        return `## ${data.title}\n**${date}**\n\n${data.content}\n\n${imgHtml}${linkHtml}\n\n---`;
                    }).join('\n\n');
                } else {
                    mdText = "### No New Updates\nThe universe is quiet for now...";
                }
            } else {
                // Standard System Docs (Terms, Privacy, About)
                let docRef = doc(db, 'system_docs', docId);
                let snap = await getDoc(docRef);

                if (snap.exists()) {
                    const data = snap.data();
                    let content = data.content || '';
                    let title = data.title || docId;

                    // [NEW] I18n Cache (Pre-translated in Admin)
                    const currentLang = localStorage.getItem('mirinae_lang') || 'ko';
                    if (currentLang !== 'ko' && data.translations?.[currentLang]) {
                        content = data.translations[currentLang].content || content;
                        title = data.translations[currentLang].title || title;
                    }

                    mdText = content;
                    displayTitle = titleOverride || title;
                }
            }

            if (mdText) {
                // Update Title
                header.querySelector('#modal-title').innerHTML = displayTitle;

                // Render Markdown
                contentInner.innerHTML = parseMarkdown(mdText);

                // Add simple fade-in effect to content
                contentInner.animate([
                    { opacity: 0, transform: 'translateY(10px)' },
                    { opacity: 1, transform: 'translateY(0)' }
                ], { duration: 600, easing: 'ease-out' });

            } else {
                // Not Found
                header.querySelector('#modal-title').innerText = "Signal Lost";
                contentInner.innerHTML = `
                    <div class="h-full flex flex-col items-center justify-center text-zinc-400 py-20">
                        <span class="material-symbols-outlined text-6xl mb-4 opacity-50 text-red-400" >satellite_alt</span>
                        <h2 class="text-xl font-bold text-white mb-2">Document Not Found</h2>
                        <p class="text-zinc-500">The requested data fragment could not be retrieved from the void.</p>
                        <p class="text-xs mt-6 opacity-30 font-mono border px-2 py-1 rounded border-white/10">ERR_ID: ${docId}</p>
                    </div>
                `;
            }

        } catch (e) {
            console.error("InfoModal Load Error:", e);
            header.querySelector('#modal-title').innerText = "System Error";
            contentInner.innerHTML = `
                <div class="p-6 bg-red-900/20 border border-red-500/30 rounded-2xl text-center">
                    <p class="font-bold text-red-400 text-lg">Transmission Failed</p>
                    <p class="text-sm mt-2 text-red-300/70 font-mono">${e.message}</p>
                </div>
            `;
        }
    }
};


